/*
 * What a rendered body needs from outside its tree, handed down once per surface: who an `@`
 * mention resolves to, the groups behind a group chip, how a link is navigated, and how the tokens
 * a module registered with the server's parser are drawn. It is one context so that a row does not
 * subscribe to the store three hundred times over, and its identity is load-bearing: a new value
 * re-renders every body under the provider, so a surface builds it once and keeps it while nothing
 * a body draws has changed (chat's `renderContext.ts`).
 */

import { createContext, useContext, type ReactNode } from "react"
import type { Mention, Permalink } from "./nodes.ts"

/**
 * How a module draws one kind of mention it registered (`core.MentionTokenDef` on the server):
 * chat's `#slug` becomes a link for a reader who has that conversation, its `@channel` a group chip.
 * `insideLink` is true while the token sits in a markdown link's label, where a second anchor may
 * not go.
 */
export type MentionDrawing = (mention: Mention, where: { insideLink: boolean }) => ReactNode

export interface RenderContextValue {
	/** Display names by account id, for a resolved `@` mention. */
	names: Record<number, string>
	/**
	 * Profile picture addresses by account id, for every avatar a surface draws. It is a map like the
	 * names beside it, and for the same reason: a conversation draws hundreds of avatars and must not
	 * ask the store once per row. A person with no picture is absent from it, which is what `Avatar`
	 * renders as the initials.
	 */
	avatars?: Record<number, string> | undefined
	/** Group handle and member names by group id, for the group chip's title. */
	groups: Record<number, { displayName: string; memberIds: number[] }>
	/** Client-side navigation for an internal href; without it an anchor behaves normally. */
	navigate?: ((path: string) => void) | undefined
	/**
	 * Opens a person's profile. A resolved `@` mention is a button while a surface passed it and
	 * plain text while it did not — a chip that looks like a control and does nothing is worse than
	 * one that does not look like a control at all (X-A1 D13).
	 */
	openProfile?: ((userId: number) => void) | undefined
	/**
	 * The drawings of the mention kinds modules registered, by kind. A kind with no drawing here is
	 * the characters the author typed: a body shown by a module that does not know a token draws it
	 * as text rather than as a chip that goes nowhere.
	 */
	mentionKinds?: Readonly<Record<string, MentionDrawing>> | undefined
	/**
	 * Where a permalink node leads, or null when this reader has no address for it. The route a
	 * permalink names belongs to the module whose messages it points at; without this a permalink
	 * is a label.
	 */
	permalinkHref?: ((perma: Permalink) => string | null) | undefined
}

const EMPTY_CONTEXT: RenderContextValue = { names: {}, groups: {} }

const RenderContext = createContext<RenderContextValue>(EMPTY_CONTEXT)

export const RenderContextProvider = RenderContext.Provider

export function useRenderContext(): RenderContextValue {
	return useContext(RenderContext)
}

/**
 * Whether the body is being drawn on a surface a person can act on. The same tree is rendered to a
 * string for the clipboard's HTML flavour with no provider around it, and there a control is dead
 * markup — so a block with chrome of its own asks before drawing it.
 */
export function useHasRenderSurface(): boolean {
	return useContext(RenderContext) !== EMPTY_CONTEXT
}

/**
 * True while a node is being drawn inside an anchor. A markdown link's label is an inline run like
 * any other, so it can carry a token that is itself a link — and an `<a>` inside an `<a>` is
 * invalid, with the browser free to resolve the click to either.
 */
export const InsideLink = createContext(false)

/**
 * Whether the literal text of this body draws its invisible characters as chips (invisible.ts).
 * `MarkdownBody` sets it from its `revealInvisible` prop, and every leaf that prints characters the
 * author wrote — a text run, code, an image's name — reads it.
 */
export const RevealInvisible = createContext(false)
